
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TabsContent } from "@/components/ui/tabs";
import { useJobForm } from "./hooks/useJobForm";
import { BasicInformationTab } from "./form-tabs/BasicInformationTab";
import { JobDetailsTab } from "./form-tabs/JobDetailsTab";
import { QualificationsTab } from "./form-tabs/QualificationsTab";
import { AdditionalInfoTab } from "./form-tabs/AdditionalInfoTab";
import { FormTabs } from "./form-tabs/FormTabs";
import { JobDetail, JobFormData } from "./types/jobTypes";
import { useEffect, useState, useRef } from "react";
import { toast } from "@/hooks/use-toast";

interface JobOfferEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedJob: JobFormData) => Promise<boolean>;
  job: JobDetail;
}

export const JobOfferEditModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  job 
}: JobOfferEditModalProps) => {
  const wasOpen = useRef(false);
  
  const {
    jobData,
    setJobData,
    isSubmitting,
    setIsSubmitting,
    activeTab,
    setActiveTab,
    selectedLanguages,
    showOtherLanguageField,
    showBudgetPerm,
    showBudgetFreelance,
    handleSelectCompany,
    handleSelectContact,
    handleSkillsKeyDown,
    handleLanguageChange,
    handleEmploymentTypeChange,
    handleTimeCommitmentChange,
    handleInputChange,
    handleSubmit,
    handlePasteFromClipboard,
    isEmploymentTypeSelected,
    resetForm
  } = useJobForm(job, onSave);
  
  useEffect(() => {
    if (isOpen && job) {
      console.log("Initializing form with job data in modal:", job);
      
      let employmentTypeFromJob = job.employmentType || "";
      
      const languages = job.languageRequirements ? 
                      job.languageRequirements.split(',').map(lang => lang.trim()) : 
                      [];
      
      const newJobData: JobFormData = {
        title: job.title || "",
        companyId: job.company?.id || "",
        companyName: job.company?.name || "",
        description: job.description || "",
        aiSummary: job.aiSummary || "", 
        skills: job.skills ? job.skills.join(", ") : "",
        contactId: job.contactId,
        
        employmentType: employmentTypeFromJob,
        timeCommitment: job.timeCommitment || "",
        contractDuration: job.contractDuration || "",
        startDate: job.startDate || "",
        endDate: job.endDate || "",
        seniorityLevel: job.seniorityLevel || "",
        budget: job.budget || "",
        budgetFreelance: job.budgetFreelance || "",
        locationType: job.locationType || "",
        workplaceBreakdown: job.workplaceBreakdown || "",
        hardSkills: job.hardSkills ? job.hardSkills.join(", ") : "",
        softSkills: job.softSkills ? job.softSkills.join(", ") : "",
        educationLevel: job.educationLevel || "",
        yearsOfExperience: job.yearsOfExperience || "",
        languageRequirements: job.languageRequirements || "",
        extraLang: job.extraLang || "",
        cultureTags: job.cultureTags ? job.cultureTags.join(", ") : "",
        applicationDeadline: job.applicationDeadline || "",
        jobPostingDate: job.jobPostingDate || "",
        source: job.source || "",
        assignedRecruiter: job.assignedRecruiter || "",
        employmentClassification: job.employmentClassification || "",
        notes: job.notes || ""
      };
      
      setJobData(newJobData);
      wasOpen.current = true;
    } else if (!isOpen && wasOpen.current) {
      const timeout = setTimeout(() => {
        resetForm();
        wasOpen.current = false;
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, job, setJobData, resetForm]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    console.log("JobOfferEditModal handleFormSubmit called", e);
    e.preventDefault();
    
    let isExplicitSave = false;
    let isSubmitButtonClick = false;

    if (e.nativeEvent?._isSubmitButtonClick) {
      isSubmitButtonClick = true;
    }

    if (e.nativeEvent?._isExplicitSave)
      isExplicitSave = true;
    
    console.log("Form submission flags:", { isExplicitSave, isSubmitButtonClick });
      
    if (isExplicitSave || isSubmitButtonClick) {
      try {
        setIsSubmitting(true);
        console.log("Submitting form data for edit:", jobData);
        const success = await handleSubmit(e, job.id);
        console.log("Form submission result:", success);
        
        if (success) {
          toast({
            title: "Success",
            description: "Job offer has been updated successfully"
          });
          onClose();
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast({
          title: "Error",
          description: "Failed to update job offer: " + (error instanceof Error ? error.message : "Unknown error"),
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log("Not submitting form - not triggered by save or submit");
    }
  };

  if (!isOpen) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()} modal>
      <SheetContent className="sm:max-w-[75%] p-0 flex flex-col overflow-hidden" onEscapeKeyDown={(e) => e.preventDefault()}>
        <SheetHeader className="p-6 pb-0">
          <SheetTitle>Edit Job Offer</SheetTitle>
        </SheetHeader>
        <div className="px-0 pt-4 flex-grow flex flex-col overflow-hidden">
          <FormTabs 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            onCancel={onClose}
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
            isCreateMode={false}
          >
            <TabsContent value="basic" className="h-full px-6">
              <BasicInformationTab 
                jobData={jobData}
                handleInputChange={handleInputChange}
                handleSelectCompany={handleSelectCompany}
                handleSelectContact={handleSelectContact}
                handlePasteFromClipboard={handlePasteFromClipboard}
                handleSkillsKeyDown={handleSkillsKeyDown}
                handleEmploymentTypeChange={handleEmploymentTypeChange}
                isEmploymentTypeSelected={isEmploymentTypeSelected}
              />
            </TabsContent>
            
            <TabsContent value="details" className="h-full px-6">
              <JobDetailsTab
                jobData={jobData}
                handleInputChange={handleInputChange}
                handleSkillsKeyDown={handleSkillsKeyDown}
                handleEmploymentTypeChange={handleEmploymentTypeChange}
                handleTimeCommitmentChange={handleTimeCommitmentChange}
                isEmploymentTypeSelected={isEmploymentTypeSelected}
                showBudgetPerm={showBudgetPerm}
                showBudgetFreelance={showBudgetFreelance}
              />
            </TabsContent>
            
            <TabsContent value="qualifications" className="h-full px-6">
              <QualificationsTab
                jobData={jobData}
                handleInputChange={handleInputChange}
                handleSkillsKeyDown={handleSkillsKeyDown}
                handleLanguageChange={handleLanguageChange}
                selectedLanguages={selectedLanguages}
                showOtherLanguageField={showOtherLanguageField}
              />
            </TabsContent>
            
            <TabsContent value="metadata" className="h-full px-6">
              <AdditionalInfoTab
                jobData={jobData}
                handleInputChange={handleInputChange}
              />
            </TabsContent>
          </FormTabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};
